import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInput } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { FieldArray, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import { newPreferenceRow } from 'services/TherapyGroupPreferenceRows';

// One row per segment window: segment, Start (required), End (optional, inclusive), remove.
// Saved rows keep their segment; Start and End can change on any row. Removing a window
// that has not started cancels it; removing one in effect ends it now.

// The same "name - id - Brand/Generic" text the segment dropdown shows for a selected option.
const segmentLabel = (option, segmentId) =>
    option
        ? [option.name, option.secondaryData, option.rightSecondaryData].filter(Boolean).join(' - ')
        : segmentId;

export const PreferenceWindowRows = ({ options, isDisabled }) => {
    const formik = useFormikContext();
    const rowsError = typeof formik.errors.rows === 'string' ? formik.errors.rows : undefined;

    return (
        <FieldArray name="rows">
            {(arrayHelpers) => (
                <>
                    {formik.values.rows.map((row, index) => (
                        <div
                            key={row.rowId}
                            className="sds_therapy-group__preference-row flex__gap--small"
                        >
                            {row.isSaved ? (
                                <TextInput
                                    id={`rows.${index}.segmentLabel`}
                                    name={`rows.${index}.segmentLabel`}
                                    label="Payer Segment"
                                    required
                                    disabled
                                    value={segmentLabel(
                                        options.find(
                                            (option) => option.segment_id === row.segmentId,
                                        ),
                                        row.segmentId,
                                    )}
                                />
                            ) : (
                                <SuggestSingleSelectFormik
                                    id={`rows.${index}.segmentId`}
                                    label="Payer Segment"
                                    required
                                    disabled={isDisabled}
                                    disableAutoPopulate
                                    values={options}
                                    displaySecondaryDataBelowOption
                                    selectedItem={options.find(
                                        (option) => option.segment_id === row.segmentId,
                                    )}
                                    onChange={(item) =>
                                        formik.setFieldValue(
                                            `rows.${index}.segmentId`,
                                            item?.segment_id ?? null,
                                        )
                                    }
                                />
                            )}
                            <DateInputFormik
                                id={`rows.${index}.start`}
                                label="Start"
                                required
                                disabled={isDisabled}
                            />
                            <DateInputFormik
                                id={`rows.${index}.end`}
                                label="End"
                                disabled={isDisabled}
                            />
                            {!isDisabled && (
                                <Icon
                                    className="margin__top--large"
                                    title="Remove segment"
                                    icon={IconNames.CROSS}
                                    intent={Icon.Intent.SECONDARY}
                                    onClick={() => arrayHelpers.remove(index)}
                                />
                            )}
                        </div>
                    ))}
                    {rowsError && (
                        <InlineMessage intent={InlineMessage.Intent.ERROR} label={rowsError} />
                    )}
                    <ActionButtonText
                        label={
                            formik.values.rows.length > 0 ? 'Add Another Segment' : 'Add Segment'
                        }
                        leftIcon={IconNames.ADD}
                        disabled={isDisabled}
                        onClick={() => arrayHelpers.push(newPreferenceRow(uuidv4()))}
                    />
                </>
            )}
        </FieldArray>
    );
};

PreferenceWindowRows.propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({ segment_id: PropTypes.string })).isRequired,
    isDisabled: PropTypes.bool.isRequired,
};
